import React from 'react';
import { Layout } from '../components/Layout';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Book, MessageCircle, Search, Shield, Phone, Mail, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const HelpCenterPage = () => {
  usePageMetadata();

  const categories = [
    {
      icon: Search,
      title: "Finding a Rental",
      description: "Learn how to search, filter, and find the perfect mid-term rental.",
      link: "/faq#renters"
    },
    {
      icon: Book,
      title: "Listing Your Property",
      description: "Guide for property owners on creating and managing listings.",
      link: "/owner-guide"
    },
    {
      icon: Shield,
      title: "Payments & Security",
      description: "Information about secure payments, deposits, and refunds.",
      link: "/faq#payments"
    },
    {
      icon: MessageCircle,
      title: "Messaging & Communication",
      description: "How to communicate with owners or renters through the platform.",
      link: "/faq#account"
    }
  ];

  const quickLinks = [
    { title: "How It Works", href: "/how-it-works" },
    { title: "FAQ", href: "/faq" },
    { title: "Owner Guide", href: "/owner-guide" },
    { title: "Pricing", href: "/pricing" },
    { title: "Terms of Service", href: "/terms" },
    { title: "Privacy Policy", href: "/privacy" }
  ];

  return (
    <Layout>
      <div className="bg-gradient-to-b from-slate-50 to-white min-h-screen py-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Help Center
            </h1>
            <p className="text-xl text-slate-600">
              Find answers and get support for Stay Pair Homes
            </p>
          </div>

          {/* Categories */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {categories.map((category, index) => (
              <Link
                key={index}
                to={category.link}
                className="bg-white rounded-xl border border-slate-200 p-6 hover:border-trust-blue hover:shadow-md transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-trust-blue/10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-trust-blue/20 transition-colors">
                    <category.icon className="w-6 h-6 text-trust-blue" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-trust-blue transition-colors">
                      {category.title}
                    </h3>
                    <p className="text-slate-600 text-sm">{category.description}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Quick Links */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-12">
            <h2 className="font-semibold text-slate-900 mb-4">Quick Links</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {quickLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.href}
                  className="flex items-center gap-2 text-trust-blue hover:underline text-sm"
                >
                  <ExternalLink className="w-3 h-3" />
                  {link.title}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact Support */}
          <div className="bg-slate-900 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">Need More Help?</h2>
            <p className="text-slate-300 mb-6">
              Our support team is here to assist you with any questions.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a 
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-3 bg-trust-blue text-white rounded-xl font-medium hover:bg-trust-blue-hover transition-colors"
              >
                <Mail className="w-4 h-4 mr-2" />
                Contact Support
              </a>
              <a 
                href="/faq"
                className="inline-flex items-center justify-center px-6 py-3 bg-slate-700 text-white rounded-xl font-medium hover:bg-slate-600 transition-colors"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Browse FAQ
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HelpCenterPage;
